<template>
  <div style="margin-top: 30px">
    <!-- Search and Filter Section -->
    <div class="filter-section">
      <a-row :gutter="16">
        <a-col :span="6">
          <p style="margin: 0px">Date Range</p>
          <a-range-picker
            v-model="filters.dateRange"
            placeholder="['Start Date', 'End Date']"
            style="width: 100%"
            format="YYYY-MM-DD"
            :allowClear="true"
          />
        </a-col>
      </a-row>
    </div>

    <!-- Transactions Table -->
    <a-table
      :columns="transactionColumns"
      :data-source="transactions"
      :pagination="pagination"
      :loading="transactionLoading"
      row-key="id"
      :scroll="{ x: 'max-content' }"
      @change="handleTableChange"
    >
      <template slot="amount" slot-scope="text, record">
        <span
          :style="{
            color: record.isCredit === 1 ? '#10b981' : '#ef4444',
          }"
        >
          {{ record.isCredit === 1 ? "+ " : "- "
          }}{{ formatAmount(text) }}
        </span>
      </template>
    </a-table>
  </div>
</template>

<script>
import moment from "moment";
import sellersService from "../../../api/sellers.service";
import { formatAmount } from "../../../utils/index.js";

export default {
  name: "Transactions",
  props: {
    sellerId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      filters: {
        dateRange: null, // Date range picker value
      },
      pagination: {
        pageIndex: 1,
        pageSize: 10,
        total: 0,
        showTotal: (total, range) =>
          `Showing ${range[0]}-${range[1]} of ${total} transactions`,
        showSizeChanger: false,
      },
      transactions: [],
      transactionLoading: false,
      transactionColumns: [
        {
          title: "Date",
          dataIndex: "createdAt",
          key: "createdAt",
          customRender: (text) => moment(text).format("MMM DD, YYYY"),
        },
        {
          title: "Type",
          dataIndex: "isCredit",
          key: "type",
          customRender: (text) => (text === 1 ? "Credit" : "Debit"),
        },
        {
          title: "Method",
          dataIndex: "methodType",
          key: "methodType",
        },
        {
          title: "Amount",
          dataIndex: "amount",
          key: "amount",
          scopedSlots: { customRender: "amount" },
        },
      ],
    };
  },
  watch: {
    sellerId: {
      handler() {
        if (this.sellerId) {
          this.fetchTransactions();
        }
      },
      immediate: true,
    },
    "filters.dateRange"() {
      this.pagination.pageIndex = 1; // Reset to first page when filters change
      this.fetchTransactions();
    },
  },
  methods: {
    formatAmount,
    async fetchTransactions() {
      if (!this.sellerId) return;

      this.transactionLoading = true;
      try {
        // Convert dateRange to startDate and endDate
        let startDate = "";
        let endDate = "";

        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          // Create Date objects with time 00:00:00 for start date
          const startDateObj = new Date(this.filters.dateRange[0].format('YYYY-MM-DD'));
          startDateObj.setHours(0, 0, 0, 0);
          startDate = startDateObj;

          // Create Date objects with time 23:59:59 for end date
          const endDateObj = new Date(this.filters.dateRange[1].format('YYYY-MM-DD'));
          endDateObj.setHours(23, 59, 59, 999);
          endDate = endDateObj;
        }

        const params = {
          startDate: startDate,
          endDate: endDate,
          pageIndex: this.pagination.pageIndex,
          pageSize: this.pagination.pageSize,
        };

        const response = await sellersService.getSellerWalletTransactions(
          this.sellerId,
          params
        );


        if (response.status === 200 && response.data?.data) {
          const data = response.data.data;
          console.log("Response data:", data.results);
          this.transactions = data.results || [];
          console.log("Transactions data:", this.transactions);
          this.pagination.total = data.totalCount || 0;
        } else {
          this.transactions = [];
          this.pagination.total = 0;
          this.$message.warning("No transactions found");
        }
      } catch (error) {
        console.error("Error fetching wallet transactions:", error);
        this.$message.error("Error loading transactions");
        this.transactions = [];
        this.pagination.total = 0;
      } finally {
        this.transactionLoading = false;
      }
    },

    handleTableChange(pagination) {
      if (pagination.pageIndex !== this.pagination.pageIndex) {
        this.pagination.pageIndex = pagination.pageIndex;
        this.fetchTransactions();
      }
    },

    getExportData() {
      // Return formatted data for Excel export
      return this.transactions.map(transaction => ({
        "Date": moment(transaction.createdAt).format("MMM DD, YYYY"),
        "Type": transaction.isCredit === 1 ? "Credit" : "Debit",
        "Method": transaction.methodType || "N/A",
        "Amount": formatAmount(transaction.amount)
      }));
    },
  },
};
</script>

<style scoped>
/* Standard filter section styling */
.filter-section {
  background: #fff;
  padding: 24px;
  border-radius: 8px;
  margin-bottom: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.filter-section .ant-row {
  margin-bottom: 0;
}

.filter-section .ant-col {
  margin-bottom: 16px;
}

.filter-section .ant-col:last-child {
  margin-bottom: 0;
}

a-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #374151;
}

.table-section {
  background: #fff;
  padding: 24px;
  border-radius: 8px;
}

.table-section h3 {
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

/* Responsive */
@media (max-width: 768px) {
  .filter-section .ant-col {
    margin-bottom: 16px;
  }
}
</style>
